import { Modal, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useContext, useMemo } from "react";
import { createUserSchema } from "../../utils/validation/schemas/userSchema";
import { Formik, Form } from "formik";
import { BiX } from "react-icons/bi";
import TextfieldWrapper from "../Form/FormUI/Textfield";
import CustomButton from "../btn/CustomButton";
import FormInput from "../reports/information/FormInput";
import AddressAutocomplete from "../Form/AddressAutoComplete";
import UserAssignRoleSelector from "./UserAssignRoleSelector";
import { useUserWrite } from "../../utils/hooks/useUsers";
import { AuthContext } from "../../contexts/AuthContext";
import PLATFORM_TYPES_ENUM from "../../lib/enums/platformTypes";

// Convert to sentence case while preserving short acronyms like UK/CEO.
const sentenceCase = (val) => {
  if (!val && val !== "") return val;
  const s = String(val).trim();
  if (s.length === 0) return s;
  if (/^[A-Z]{2,3}$/.test(s)) return s;
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};

const uppercaseValue = (val) => {
  if (!val && val !== "") return val;
  return String(val).trim().toUpperCase();
};

const UserWriteModal = ({ open, defaultValues, onClose }) => {
  const { t, i18n } = useTranslation();

  const initialValues = {
    id: defaultValues?.id,
    first_name: sentenceCase(defaultValues?.first_name ?? ""),
    last_name: sentenceCase(defaultValues?.last_name ?? ""),
    email: defaultValues?.email ?? "",
    address: defaultValues?.address ?? "",
    city: defaultValues?.city ?? "",
    postal_code: defaultValues?.postal_code ?? "",
    country: uppercaseValue(defaultValues?.country ?? ""),
    phone_no: defaultValues?.phone_no ?? "",
    company_name: defaultValues?.company_name ?? "",
    vat_number: defaultValues?.vat_number ?? "",
    role_id: defaultValues?.role?.id,
    parent_user_id: defaultValues?.parent_user?.id,
    platform_type: defaultValues?.role?.platform_type,
  };

  const userSchema = useMemo(() => createUserSchema(t), [t]);

  const mode = !!defaultValues ? "edit" : "create";
  const { mutateAsync } = useUserWrite(mode);

  const { user, isLoading: isUserLoading } = useContext(AuthContext);
  const isSelf = user?.userId === defaultValues?.id;
  const canEditEmail = !isSelf && !isUserLoading;

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true);
      const payload = {
        ...values,
        first_name: sentenceCase(values.first_name),
        last_name: sentenceCase(values.last_name),
        country: uppercaseValue(values.country),
      };
      if (!payload.parent_user_id) {
        delete payload.parent_user_id;
      }
      await mutateAsync(payload);
      handleClose();
    } catch {
      return;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box>
        <Formik initialValues={initialValues} validationSchema={userSchema} onSubmit={handleSubmit}>
          {({
            isSubmitting,
            submitForm,
            isValid,
            values,
            setFieldValue,
            validateField,
            errors,
            touched,
            setFieldTouched,
          }) => {
            const text =
              mode === "create"
                ? {
                    title: t("userManagement.addUser.title"),
                    description: t("userManagement.addUser.description"),
                  }
                : {
                    title: t("userManagement.editUser.title"),
                    description: t("userManagement.editUser.description"),
                  };

            const isClient = values.platform_type === PLATFORM_TYPES_ENUM.CLIENT;
            return (
              <Form className="flex flex-col gap-1 bg-white rounded-xl md:w-[45rem] max-h-[90vh]">
                <div className="flex gap-x-2 px-6 pt-5 pb-3">
                  <div className="grow">
                    <h1 className="font-bold text-lg">{text.title}</h1>
                    <p className="text-sm text-gray-600 mt-1">{text.description}</p>
                  </div>
                  <button
                    className="text-gray-400 hover:bg-gray-100 hover:text-gray-900 h-min w-min rounded-lg"
                    variant="outline"
                    onClick={handleClose}
                  >
                    <BiX fontSize="2rem" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-4 px-6 pb-2 min-h-0 flex-1 relative overflow-y-scroll custom-scrollbar">
                  <FormInput
                    label={t("userManagement.data.firstName")}
                    type="text"
                    name="first_name"
                    value={values.first_name}
                    placeholder={t("userManagement.data.firstNamePlaceholder")}
                    onChange={(e) => setFieldValue("first_name", e.target.value, true)}
                    onBlur={(e) => {
                      const formatted = sentenceCase(e.target.value);
                      setFieldValue("first_name", formatted, true);
                      setFieldTouched("first_name", true, false);
                    }}
                    error={touched.first_name && errors.first_name}
                    required={true}
                  />
                  <FormInput
                    label={t("userManagement.data.lastName")}
                    type="text"
                    name="last_name"
                    value={values.last_name}
                    placeholder={t("userManagement.data.lastNamePlaceholder")}
                    onChange={(e) => setFieldValue("last_name", e.target.value, true)}
                    onBlur={(e) => {
                      const formatted = sentenceCase(e.target.value);
                      setFieldValue("last_name", formatted, true);
                      setFieldTouched("last_name", true, false);
                    }}
                    error={touched.last_name && errors.last_name}
                    required={true}
                  />
                  <TextfieldWrapper
                    name="email"
                    disabled={!canEditEmail}
                    required={true}
                    label={t("userManagement.data.email")}
                    placeholder={t("userManagement.data.emailPlaceholder")}
                    type="email"
                  />
                  <FormInput
                    label={t("userManagement.data.phone")}
                    type="phone"
                    name="phone_no"
                    value={values.phone_no}
                    onChange={(e) => setFieldValue("phone_no", e.target.value, true)}
                    onBlur={() => setFieldTouched("phone_no", true, false)}
                    error={touched.phone_no && errors.phone_no}
                    required={true}
                  />
                  <TextfieldWrapper
                    name="company_name"
                    required={isClient}
                    label={t("userManagement.data.companyName")}
                    placeholder={t("userManagement.data.companyNamePlaceholder")}
                    type="text"
                  />
                  <TextfieldWrapper
                    name="vat_number"
                    required={isClient}
                    label={t("userManagement.data.vatNumber")}
                    placeholder={t("userManagement.data.vatNumberPlaceholder")}
                    type="text"
                  />
                  {mode === "create" && (
                    <div className="col-span-2 grid grid-cols-2 gap-4">
                      <UserAssignRoleSelector
                        parentUser={values.parent_user_id}
                        selectedRoleId={values.role_id}
                        onRoleChange={(role) => {
                          setFieldValue("role_id", role?.id, true);
                          setFieldValue("platform_type", role?.platform_type, true);
                        }}
                        onParentUserChange={(userId) =>
                          setFieldValue("parent_user_id", userId, true)
                        }
                      />
                    </div>
                  )}
                  <div className="col-span-2">
                    <AddressAutocomplete
                      required={isClient}
                      name="address"
                      label={t("userManagement.data.address")}
                      placeholder={t("userManagement.data.addressPlaceholder")}
                      value={values.address}
                      onChange={async (e) => {
                        await setFieldValue("address", e.target.value);
                        validateField("address");
                      }}
                      error={touched.address && !!errors.address}
                      helperText={errors.address}
                      language={i18n.language === "da" ? "da" : "en"}
                      onBlur={() => setFieldTouched("address", true, false)}
                      onAddressSelect={async (addressData) => {
                        const promises = [];
                        promises.push(setFieldValue("address", addressData.address));
                        const locationIQPostalCode = addressData?.postcode ?? "";
                        const locationIQCity = addressData.city || "";
                        const locationIQCountry = addressData.country || "";

                        promises.push(setFieldValue("postal_code", locationIQPostalCode));
                        promises.push(setFieldValue("city", locationIQCity));
                        promises.push(setFieldValue("country", locationIQCountry));

                        await Promise.all(promises);
                        validateField("address");
                        validateField("postal_code");
                        validateField("city");
                        validateField("country");
                      }}
                      onAddressClear={async () => {
                        const promises = [];
                        promises.push(setFieldValue("address", ""));
                        promises.push(setFieldValue("postal_code", ""));
                        promises.push(setFieldValue("city", ""));
                        promises.push(setFieldValue("country", ""));

                        await Promise.all(promises);
                        validateField("address");
                        validateField("postal_code");
                        validateField("city");
                        validateField("country");
                      }}
                    />
                  </div>

                  <TextfieldWrapper
                    name="city"
                    required={isClient}
                    label={t("userManagement.data.city")}
                    placeholder={t("userManagement.data.cityPlaceholder")}
                    type="text"
                  />

                  <TextfieldWrapper
                    name="postal_code"
                    required={isClient}
                    label={t("userManagement.data.postalCode")}
                    placeholder={t("userManagement.data.postalCodePlaceholder")}
                    type="text"
                  />

                  <FormInput
                    label={t("userManagement.data.country")}
                    type="text"
                    name="country"
                    value={values.country}
                    placeholder={t("userManagement.data.countryPlaceholder")}
                    onChange={(e) => setFieldValue("country", e.target.value, true)}
                    onBlur={(e) => {
                      const formatted = uppercaseValue(e.target.value);
                      setFieldValue("country", formatted, true);
                      setFieldTouched("country", true, false);
                    }}
                    error={touched.country && errors.country}
                    required={isClient}
                  />
                </div>
                <div className="flex justify-end gap-2 border-t pt-4 px-6 pb-5">
                  <CustomButton variant="border" onClick={handleClose}>
                    {t("main.buttons.cancel")}
                  </CustomButton>
                  <CustomButton type="submit" disabled={isSubmitting}>
                    {t("main.buttons.saveChanges")}
                  </CustomButton>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Box>
    </Modal>
  );
};

export default UserWriteModal;
